import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {
  BartenderLabelTemplate,
  BartenderServer,
  BartenderServerItem,
  BartenderServerLabelTemplate,
  BartenderServerPrinter,
  fetchBartenderLabelTemplates,
  fetchBartenderPrinters,
} from 'label-printing/BartenderUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_LIGHT, ORANGE_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas, isOptionSelectable} from 'ui-library/menus/MenuUtil';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {isAbortError} from 'util/Net';
import strUtil from 'util/strUtil';

interface LimitOptionsToServer {
  bartenderServerUrl: string;
  tooltip: string;
}

interface Props<Item> {
  bartenderServers: Array<BartenderServer>;
  limitOptionsToServer?: LimitOptionsToServer | null;
  fetchItems: (baseUrl: string, abortController?: AbortController) => Promise<Array<Item>>;
  getItemOptionName: (item: Item) => string;
  defaultSelectedItem: BartenderServerItem<Item> | null;
  selectedItem: BartenderServerItem<Item> | null;
  onSelectItem: (item: BartenderServerItem<Item>) => void;
  defaultMessage: string;
  shouldHideOption?: (item: Item) => boolean;
  dataTestAttrs?: DataTestAttrs;
}

function BartenderGenericPrintModalDropdown<Item>(props: Props<Item>): JSX.Element {
  const [items, setItems] = useState<{[bartenderUrl: string]: Array<Item>}>({});
  const [errors, setErrors] = useState<{[bartenderUrl: string]: string}>({});

  /**
   * This function is called when the response comes back from the BarTender server (`props.fetchItems()`).
   * It selects the default item by calling `props.onSelectItem(props.defaultSelectedItem)` if:
   *  1. nothing is selected currently
   *  2. there is a default selected item
   *  3. the default selected item is from the same BarTender server as the loaded items
   *  4. `limitOptionsToServer` is unset, or is the server these items were fetched from
   *  5. the default selected item is among the items returned by the server
   *
   *  Some context: https://benchling.slack.com/archives/C02M24QF1EX/p1645225808108389
   *
   * @param bartenderServer bartender server
   * @param itemz items fetched from the server
   */
  const maybeSelectDefault = (bartenderServer: BartenderServer, itemz: Array<Item>): void => {
    if (
      props.selectedItem == null &&
      props.defaultSelectedItem != null &&
      props.defaultSelectedItem.bartenderServer.url === bartenderServer.url &&
      (props.limitOptionsToServer == null ||
        bartenderServer.url === props.limitOptionsToServer.bartenderServerUrl) &&
      itemz.some((item) => isEqual(item, props.defaultSelectedItem?.item))
    ) {
      props.onSelectItem(props.defaultSelectedItem);
    }
  };

  useOnMount((): (() => void) => {
    const abortController = new AbortController();
    let isMounted = true;
    for (const bartenderServer of props.bartenderServers) {
      props
        .fetchItems(bartenderServer.url, abortController)
        // eslint-disable-next-line no-loop-func
        .then((itemz) => {
          if (isMounted) {
            setItems((prevItems) => ({
              ...prevItems,
              [bartenderServer.url]: itemz.filter(
                (i) => props.shouldHideOption == null || !props.shouldHideOption(i)
              ),
            }));
            maybeSelectDefault(bartenderServer, itemz);
          }
        })
        // eslint-disable-next-line no-loop-func
        .catch((reason: unknown) => {
          if (isMounted && !isAbortError(reason)) {
            setErrors((prevErrors) => ({...prevErrors, [bartenderServer.url]: String(reason).toString()}));
          }
        });
    }

    return function cleanup() {
      // Avoid "Warning: Can't perform a React state update on an unmounted component...."
      isMounted = false;
      abortController.abort();
    };
  });

  const getOptionDatasForBartenderServer = (bartenderServer: BartenderServer): Array<OptionData> => {
    if (errors[bartenderServer.url] != null) {
      // error
      return [
        {
          name: (
            <>
              <div>{bartenderServer.url}</div>
              {bartenderServer.name && <div className="faded">{bartenderServer.name}</div>}
              <BenchlingIcon icon="warning" color={ORANGE_LIGHT} />
            </>
          ),
          isHeader: true,
          // https://benchling.slack.com/archives/C7X6N0QNN/p1590773471061700?thread_ts=1590772133.061400&cid=C7X6N0QNN
          className: css(styles.borderTopGrayLight, styles.flexColumnGap),
          extra: {headerId: bartenderServer.url},
        },
        {
          name: 'Problem communicating with BarTender server',
          disabled: true,
          value: null,
          extra: {headerId: bartenderServer.url},
        },
      ];
    } else if (items[bartenderServer.url] == null) {
      // loading
      return [
        {
          name: (
            <>
              <div>{bartenderServer.url}</div>
              {bartenderServer.name && <div className="faded">{bartenderServer.name}</div>}
            </>
          ),
          isHeader: true,
          className: css(styles.borderTopGrayLight, styles.flexColumnGap),
          extra: {headerId: bartenderServer.url},
        },
        {
          name: 'Loading...',
          disabled: true,
          value: null,
          extra: {headerId: bartenderServer.url},
        },
      ];
    } else {
      // success, list the items
      const isServerAllowed =
        props.limitOptionsToServer == null ||
        bartenderServer.url === props.limitOptionsToServer.bartenderServerUrl;
      return [
        {
          name: (
            <>
              <div>{bartenderServer.url}</div>
              {bartenderServer.name && <div className="faded">{bartenderServer.name}</div>}
            </>
          ),
          isHeader: true,
          className: css(styles.borderTopGrayLight, styles.flexColumnGap),
          extra: {headerId: bartenderServer.url},
        },
        ...items[bartenderServer.url].map((item) => ({
          name: props.getItemOptionName(item),
          value: {bartenderServer, item},
          disabled: !isServerAllowed,
          disabledMessage: !isServerAllowed ? props.limitOptionsToServer!.tooltip : undefined,
          extra: {headerId: bartenderServer.url},
        })),
      ];
    }
  };

  return (
    <SelectDropdown
      displayName={
        props.selectedItem != null ? props.getItemOptionName(props.selectedItem.item) : props.defaultMessage
      }
      menu={
        <SearchableMenu
          optionDatas={props.bartenderServers.flatMap((bartenderServer) =>
            getOptionDatasForBartenderServer(bartenderServer)
          )}
          onSelect={props.onSelectItem}
          width={MenuWidth.FULL}
          getMatchingOptionDatas={getMatchingOptionDatasWithServerMatch}
        />
      }
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

/*
 * Returns optionDatas that match a given query, either directly or in the Bartender server name/url.
 * Includes server headers for matched options.
 *
 * Exported for testing.
 */
export function getMatchingOptionDatasWithServerMatch(
  query: string,
  optionDatas: Array<OptionData>
): Array<OptionData> {
  if (query === '') {
    return optionDatas;
  }
  const allMatchedOptions = new Set(getMatchingOptionDatas(query, optionDatas));

  const serverQuery = query.trim();
  for (const optionData of optionDatas) {
    const bartenderServer: BartenderServer | null = optionData.value?.bartenderServer ?? null;
    if (
      isOptionSelectable(optionData) &&
      serverQuery &&
      bartenderServer &&
      (strUtil.caseInsensitiveContains(bartenderServer.url, serverQuery) ||
        (bartenderServer.name != null && strUtil.caseInsensitiveContains(bartenderServer.name, serverQuery)))
    ) {
      allMatchedOptions.add(optionData);
    }
  }

  const serverHeadersByUrl = optionDatas.reduce((headers: {[headerId: string]: OptionData}, option) => {
    if (option.isHeader && option.extra?.headerId != null) {
      headers[option.extra.headerId] = option;
    }
    return headers;
  }, {});
  const matchedOptionsByServerUrl = [...allMatchedOptions].reduce(
    (optionsByUrl: {[headerId: string]: Array<OptionData>}, option) => {
      if (option.extra?.headerId != null) {
        optionsByUrl[option.extra.headerId] =
          optionsByUrl[option.extra.headerId] != null
            ? [...optionsByUrl[option.extra.headerId], option]
            : [option];
      }
      return optionsByUrl;
    },
    {}
  );

  return Object.entries(matchedOptionsByServerUrl).flatMap(([headerId, options]) => [
    serverHeadersByUrl[headerId],
    ...options,
  ]);
}

interface BartenderLabelTemplateDropdownProps {
  bartenderServers: Array<BartenderServer>;
  limitOptionsToServer?: LimitOptionsToServer | null;
  defaultSelectedLabelTemplate: BartenderServerLabelTemplate | null;
  selectedLabelTemplate: BartenderServerLabelTemplate | null;
  onSelectLabelTemplate: (labelTemplate: BartenderServerLabelTemplate) => void;
}

export function BartenderLabelTemplateDropdown(props: BartenderLabelTemplateDropdownProps): JSX.Element {
  return (
    <BartenderGenericPrintModalDropdown<BartenderLabelTemplate>
      bartenderServers={props.bartenderServers}
      fetchItems={fetchBartenderLabelTemplates}
      getItemOptionName={(item) => item.relativePath}
      defaultSelectedItem={props.defaultSelectedLabelTemplate}
      selectedItem={props.selectedLabelTemplate}
      onSelectItem={props.onSelectLabelTemplate}
      defaultMessage="Select a label template..."
      limitOptionsToServer={props.limitOptionsToServer}
      dataTestAttrs={{component: 'BartenderPrintModal', element: 'labelTemplateDropdown'}}
    />
  );
}

interface BartenderPrinterDropdownProps {
  bartenderServers: Array<BartenderServer>;
  limitOptionsToServer?: LimitOptionsToServer | null;
  defaultSelectedPrinter: BartenderServerPrinter | null;
  selectedPrinter: BartenderServerPrinter | null;
  onSelectPrinter: (printer: BartenderServerPrinter) => void;
}

export function BartenderPrinterDropdown(props: BartenderPrinterDropdownProps): JSX.Element {
  return (
    <BartenderGenericPrintModalDropdown<string>
      bartenderServers={props.bartenderServers}
      fetchItems={fetchBartenderPrinters}
      getItemOptionName={(item) => item}
      defaultSelectedItem={props.defaultSelectedPrinter}
      selectedItem={props.selectedPrinter}
      onSelectItem={props.onSelectPrinter}
      defaultMessage="Select a printer..."
      // The "PDF" printer is used for print preview feature
      shouldHideOption={(printer) => printer === 'PDF'}
      limitOptionsToServer={props.limitOptionsToServer}
      dataTestAttrs={{component: 'BartenderPrintModal', element: 'printerDropdown'}}
    />
  );
}

const styles = StyleSheet.create({
  flexColumnGap: {
    columnGap: '6px',
  },
  borderTopGrayLight: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
});
